import axios from 'axios';
import useStore from '@/store/useStore';
import { getSelectedMerchantId } from '@/lib/routes';

function runtimeApiBase() {
  const configured = (process.env.REACT_APP_BACKEND_URL || '').trim();
  const hasWindow = typeof window !== 'undefined';
  const origin = hasWindow ? window.location.origin : '';
  const hostname = hasWindow ? window.location.hostname : '';
  const localHosts = new Set(['localhost', '127.0.0.1']);
  const isDev = process.env.NODE_ENV !== 'production';
  const isDevTunnel = hostname.endsWith('devtunnels.ms');

  // In development (including dev tunnels), always use relative /api.
  // This routes through webpack devServer proxy and avoids device-localhost issues.
  if (isDev || isDevTunnel) {
    return '/api';
  }

  // No explicit backend set: use same-origin /api (works with dev proxy/tunnels).
  if (!configured) {
    return '/api';
  }

  try {
    const configuredUrl = new URL(configured);
    if (hasWindow) {
      const runtimeHost = window.location.hostname;
      const runtimeIsLocal = localHosts.has(runtimeHost);
      const configuredIsLocal = localHosts.has(configuredUrl.hostname);

      // If frontend is opened remotely (tunnel/mobile) but backend is configured as localhost,
      // use same-origin /api so devServer proxy can forward to local backend.
      if (!runtimeIsLocal && configuredIsLocal) {
        return '/api';
      }
    }
  } catch (e) {}

  return `${configured.replace(/\/+$/, '')}/api`;
}

const API = runtimeApiBase();
if (process.env.NODE_ENV !== 'production') {
  // Helpful during tunnel debugging to verify base in browser console.
  // eslint-disable-next-line no-console
  console.info('[api] baseURL:', API);
}

const apiClient = axios.create({
  baseURL: API,
  timeout: 30000,
});

// Dynamic token and merchant-context interceptor from Zustand state.
apiClient.interceptors.request.use((config) => {
  const state = useStore.getState();
  const url = config.url || '';
  config.headers = config.headers || {};
  if (typeof window !== 'undefined') {
    config.headers['X-Frontend-Origin'] = window.location.origin;
  }

  if (url.includes('/merchant') || url.includes('/chat') || url.includes('/campaign')) {
    if (state.merchantToken) {
      config.headers.Authorization = `Bearer ${state.merchantToken}`;
    }
  } else if (url.includes('/guest')) {
    if (state.guestToken) {
      config.headers.Authorization = `Bearer ${state.guestToken}`;
    }
    const selectedMerchantId = getSelectedMerchantId();
    if (selectedMerchantId && selectedMerchantId !== 'demo') {
      config.headers['X-Merchant-Id'] = selectedMerchantId;
    }
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const requestUrl = String(error?.config?.url || '');
    const skipUrls = ['/merchant/login', '/merchant/register', '/guest/login'];

    if (status === 401 && !skipUrls.some((url) => requestUrl.includes(url))) {
      const state = useStore.getState();
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
      const inMerchantArea =
        currentPath.startsWith('/merchant') ||
        requestUrl.includes('/merchant') ||
        requestUrl.includes('/chat') ||
        requestUrl.includes('/campaign');

      if (inMerchantArea) {
        state.merchantLogout?.();
        if (typeof window !== 'undefined') {
          window.location.replace('/merchant');
        }
      } else {
        state.guestLogout?.();
        if (typeof window !== 'undefined') {
          window.localStorage.clear();
          window.sessionStorage.clear();
          window.location.href = '/store';
        }
      }
    }

    return Promise.reject(error);
  }
);

// Public guest/store context
export async function apiGetStoreInfo(merchantId) {
  const { data } = await apiClient.get(`/guest/store/${merchantId}`);
  return data.merchant;
}

export async function apiLookupStoreByNameHint(nameHint) {
  const hint = String(nameHint || '').trim();
  if (!hint) throw new Error('name_hint is required');
  const { data } = await apiClient.get(`/guest/store-lookup?name_hint=${encodeURIComponent(hint)}`);
  return data.merchant;
}

// Guest API
export async function apiGuestLogin(phone, otp = '', name = '', merchant_id, extra = {}) {
  const payload = { phone, otp, name, merchant_id, ...(extra || {}) };
  const { data } = await apiClient.post('/guest/login', payload);
  return data;
}

export async function apiGetGuestRestaurants(phone = '') {
  const query = phone ? `?phone=${encodeURIComponent(phone)}` : '';
  const { data } = await apiClient.get(`/guest/restaurants${query}`);
  return data.restaurants || [];
}

export async function apiGuestSwitchStore(merchant_id) {
  const { data } = await apiClient.post('/guest/switch-store', { merchant_id });
  return data;
}

export async function apiGetWallet() {
  const { data } = await apiClient.get('/guest/wallet');
  return data;
}

export async function apiGetTransactions() {
  const { data } = await apiClient.get('/guest/transactions');
  return data.transactions;
}

export async function apiGetRewards() {
  const { data } = await apiClient.get('/guest/rewards');
  return data;
}

export async function apiGuestUpdateProfile(payload) {
  const { data } = await apiClient.put('/guest/profile', payload);
  return data;
}

export async function apiRedeem(points = 0, channel = 'source', bill_amount = null, campaign_code = null) {
  const payload = { channel, bill_amount };
  if (Number.isFinite(Number(points))) {
    payload.points = Number(points);
  }
  if (campaign_code) payload.campaign_code = campaign_code;
  const { data } = await apiClient.post('/guest/redeem', payload);
  return data;
}

export async function apiGetRedemptionStatus(id) {
  const { data } = await apiClient.get(`/guest/redemption-status/${id}`);
  return data;
}

export async function apiScanInvoice(invoice_code) {
  const { data } = await apiClient.post('/guest/scan-invoice', { invoice_code });
  return data;
}

export async function apiGetInvoiceInfo(invoiceCode) {
  const { data } = await apiClient.get(`/invoice/${invoiceCode}`);
  return data.invoice;
}

// Merchant API
export async function apiMerchantRegister(email, password, name) {
  const { data } = await apiClient.post('/merchant/register', { email, password, name });
  return data;
}

export async function apiMerchantLogin(email, password) {
  const { data } = await apiClient.post('/merchant/login', { email, password });
  return data;
}

export async function apiMerchantMe() {
  const { data } = await apiClient.get('/merchant/me');
  return data.merchant;
}

export async function apiMerchantSetup(restaurant_name, location, logo_base64) {
  const { data } = await apiClient.put('/merchant/setup', { restaurant_name, location, logo_base64 });
  return data;
}

export async function apiMerchantTheme(brand_color, theme) {
  const { data } = await apiClient.put('/merchant/theme', { brand_color, theme });
  return data;
}

export async function apiMerchantUpdateProfile(payload) {
  const { data } = await apiClient.put('/merchant/profile', payload);
  return data;
}

export async function apiMerchantDashboard(rangeDays = 30) {
  const safeRange = [7, 30, 90].includes(Number(rangeDays)) ? Number(rangeDays) : 30;
  const { data } = await apiClient.get(`/merchant/dashboard?range_days=${safeRange}`);
  return data;
}

export async function apiMerchantCustomerActivity(customerId) {
  const { data } = await apiClient.get(`/merchant/customers/${customerId}/activity`);
  return data;
}

export async function apiMerchantAddCustomer(name, phone) {
  const { data } = await apiClient.post('/merchant/customers/create', { name, phone });
  return data;
}

export async function apiSimulateOrder(customer_id, order_id, amount) {
  const { data } = await apiClient.post('/merchant/simulate-order', { customer_id, order_id, amount });
  return data;
}

export async function apiMerchantPendingRedemptions() {
  const { data } = await apiClient.get('/merchant/redemptions/pending');
  return data.pending_redemptions || [];
}

export async function apiValidateRedemption(code, redemption_id = null) {
  const { data } = await apiClient.post('/merchant/validate-redemption', { code, redemption_id });
  return data;
}

export async function apiRejectRedemption(redemption_id, reason = '') {
  const { data } = await apiClient.post('/merchant/reject-redemption', { redemption_id, reason });
  return data;
}

export async function apiMerchantInitiateRedemption(customer_id, points, apply_bonus_on_redemption = false) {
  const { data } = await apiClient.post('/merchant/initiate-redemption', { customer_id, points, apply_bonus_on_redemption });
  return data;
}

export async function apiGenerateInvoices(count = 5) {
  const { data } = await apiClient.post('/merchant/invoices/generate-demo', { count });
  return data;
}

export async function apiDownloadInvoicePdf(count = 5) {
  const { data } = await apiClient.post('/merchant/invoices/demo-pdf', { count }, { responseType: 'blob' });
  return data;
}

export async function apiDownloadStoreQrPdf() {
  const { data } = await apiClient.get('/merchant/store-qr/pdf', { responseType: 'blob' });
  return data;
}

export async function apiMerchantCampaigns(status = 'all') {
  const query = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : '';
  const { data } = await apiClient.get(`/merchant/campaigns${query}`);
  return data;
}

export async function apiMerchantCampaignAudiencePreview(campaignSubtype) {
  const { data } = await apiClient.get(`/merchant/campaigns/audience-preview?campaign_subtype=${encodeURIComponent(campaignSubtype)}`);
  return data;
}

export async function apiCampaignSend(payload) {
  const { data } = await apiClient.post('/campaign/send', payload);
  return data;
}

export async function apiMerchantCampaignDetail(campaignId) {
  const { data } = await apiClient.get(`/merchant/campaigns/${campaignId}`);
  return data;
}

export async function apiMerchantCancelCampaign(campaignId, reason = '') {
  const { data } = await apiClient.post(`/merchant/campaigns/${campaignId}/cancel`, { reason });
  return data;
}

export async function apiMerchantAiBriefing(timeRangeDays = 7) {
  const safeRange = [7, 30, 90].includes(Number(timeRangeDays)) ? Number(timeRangeDays) : 7;
  const { data } = await apiClient.get(`/merchant/ai/briefing?time_range_days=${safeRange}`);
  return data;
}

export async function apiMerchantDecisionInsights(timeRangeDays = 7) {
  const safeRange = [7, 30, 90].includes(Number(timeRangeDays)) ? Number(timeRangeDays) : 7;
  const { data } = await apiClient.get(`/merchant/ai/decision-insights?time_range_days=${safeRange}`);
  return data;
}

export async function apiSendChat(messages, preferences = null) {
  const { data } = await apiClient.post('/chat', { messages, preferences });
  return data;
}
